/**
 * Drop-in `react-toastify` replacement, backed by the DS2026 toastStore.
 *
 * Vite aliases the bare `react-toastify` specifier to this module (see
 * vite.config.ts), so every existing `import { toast } from 'react-toastify'`
 * and the `notify*` wrappers route through the new stacked `<Toaster>` without
 * touching the ~450 call sites. Type-checking still resolves the real package
 * (the alias is bundler-only), so callers keep their original types.
 *
 * Reverting the integration = remove the alias + restore <ToastContainer/>.
 */
import type { ReactNode } from 'react'
import {
  addToast,
  dismissAll,
  dismissToast,
  isActive,
  updateToast,
  type ToastContent,
  type ToastData,
  type ToastId,
  type ToastVariant,
} from './toastStore'

export type ToastOptions = {
  autoClose?: number | false
  toastId?: ToastId
  /** react-toastify lets a bare `toast(msg, { type })` set the variant. */
  type?: string
  [key: string]: unknown
}
export type ToastContentProps = Record<string, unknown>
type Id = ToastId
// Promise message specs may be a string/node, a fn of the resolved data, or a
// react-toastify `{ render }` wrapper — resolved (with data) at settle time.
type Renderable = ReactNode | ((data?: unknown) => ReactNode)
type MsgSpec = Renderable | { render?: Renderable }
type Patch = Partial<Omit<ToastData, 'id'>>

const durationFrom = (opts?: ToastOptions): number | null | undefined => {
  if (!opts || opts.autoClose === undefined) return undefined // → store default
  if (opts.autoClose === false) return null // sticky
  return typeof opts.autoClose === 'number' ? opts.autoClose : undefined
}

const mapType = (type?: string): ToastVariant => {
  switch (type) {
    case 'success':
      return 'success'
    case 'error':
      return 'error'
    case 'warning':
    case 'warn':
      return 'warning'
    default:
      return 'info'
  }
}

const resolve = (spec: MsgSpec | undefined, data?: unknown): ReactNode => {
  if (spec == null) return undefined
  if (typeof spec === 'function') return spec(data)
  if (typeof spec === 'object' && 'render' in spec) {
    const r = spec.render
    return typeof r === 'function' ? r(data) : r
  }
  return spec as ReactNode
}

// Content (a node or a render fn) is passed THROUGH to the store untouched, so
// render functions are invoked with `closeToast` at render time, not here.
const make =
  (variant: ToastVariant) =>
  (msg: ToastContent, opts?: ToastOptions): Id =>
    addToast(
      { variant, title: msg, duration: durationFrom(opts) },
      opts?.toastId
    )

type UpdateOptions = {
  render?: ToastContent
  type?: string
  autoClose?: number | false
  data?: unknown
}
type PromiseMsgs = { pending?: MsgSpec; success?: MsgSpec; error?: MsgSpec }

type ToastApi = ((msg: ToastContent, opts?: ToastOptions) => Id) & {
  success: (msg: ToastContent, opts?: ToastOptions) => Id
  error: (msg: ToastContent, opts?: ToastOptions) => Id
  info: (msg: ToastContent, opts?: ToastOptions) => Id
  warning: (msg: ToastContent, opts?: ToastOptions) => Id
  warn: (msg: ToastContent, opts?: ToastOptions) => Id
  loading: (msg: ToastContent, opts?: ToastOptions) => Id
  dismiss: (id?: Id) => void
  isActive: (id: Id) => boolean
  update: (id: Id, opts?: UpdateOptions) => Id
  promise: <T>(
    promise: Promise<T> | (() => Promise<T>),
    msgs: PromiseMsgs,
    opts?: ToastOptions
  ) => Promise<T>
}

const api = ((msg: ToastContent, opts?: ToastOptions) =>
  addToast(
    {
      variant: opts?.type ? mapType(opts.type) : 'info',
      title: msg,
      duration: durationFrom(opts),
    },
    opts?.toastId
  )) as ToastApi

api.success = make('success')
api.error = make('error')
api.info = make('info')
api.warning = make('warning')
api.warn = make('warning')
// Loading toasts are always sticky (duration: null) until resolved via
// `update`/`promise` — matching react-toastify's `toast.loading`. Any
// `autoClose` passed in opts is intentionally ignored; only `toastId` is used.
api.loading = (msg, opts) =>
  addToast({ variant: 'loading', title: msg, duration: null }, opts?.toastId)
api.dismiss = (id) => (id == null ? dismissAll() : dismissToast(id))
api.isActive = (id) => isActive(id)
api.update = (id, opts) => {
  const patch: Patch = {}
  if (opts?.render !== undefined) patch.title = opts.render // node or render fn
  if (opts?.type) patch.variant = mapType(opts.type)
  if (opts && 'autoClose' in opts) {
    patch.duration = opts.autoClose === false ? null : opts.autoClose
  }
  updateToast(id, patch)
  return id
}
api.promise = (promise, msgs, opts) => {
  const p = typeof promise === 'function' ? promise() : promise
  const id = addToast({
    variant: 'loading',
    title: resolve(msgs.pending) ?? 'Loading…',
    duration: null,
  })
  // Distinguish unset (undefined → default 4s) from sticky (null, via
  // autoClose: false) — `?? 4000` would wrongly turn sticky into a 4s dismiss.
  const rawSettle = durationFrom(opts)
  const settle = rawSettle !== undefined ? rawSettle : 4000
  // This side-effect chain is detached from the returned `p` (callers still get
  // the original promise, matching react-toastify). The trailing .catch makes
  // sure a throw inside updateToast (e.g. a hot-reload race) is surfaced rather
  // than becoming a silent unhandled rejection.
  p.then(
    (data) =>
      updateToast(id, {
        variant: 'success',
        title: resolve(msgs.success, data) ?? 'Done',
        duration: settle,
      }),
    (err) =>
      updateToast(id, {
        variant: 'error',
        title: resolve(msgs.error, err) ?? 'Something went wrong',
        duration: settle,
      })
  ).catch((e: unknown) => console.error('[toast.promise] update failed', e))
  return p
}

export const toast = api

// No-op stand-in: the real container is the mounted <Toaster>. Kept so any
// lingering `import { ToastContainer }` still resolves.
export const ToastContainer = (): null => null
